<template>
	<div class="download-center-page">
		<!-- 搜索区 -->
		<el-card class="search-card" shadow="hover">
			<el-form :inline="true" class="search-form">
				<el-form-item label="创建时间">
					<el-date-picker
						v-model="queryTime"
						type="daterange"
						range-separator="至"
						start-placeholder="开始日期"
						end-placeholder="结束日期"
						value-format="YYYY-MM-DD HH:mm:ss"
						:default-time="[new Date(0, 0, 0, 0, 0, 0), new Date(0, 0, 0, 23, 59, 59)]"
						style="width: 320px;"
					/>
				</el-form-item>
				<el-form-item>
					<el-button type="primary" @click="handleSearch">
						<el-icon class="btn-icon"><Search /></el-icon>
						查询
					</el-button>
					<el-button @click="handleReset">
						<el-icon class="btn-icon"><Refresh /></el-icon>
						重置
					</el-button>
				</el-form-item>
			</el-form>
		</el-card>

		<!-- 列表区 -->
		<el-card class="table-card" shadow="never">
			<el-table 
				:data="downloadList" 
				v-loading="loading"
				style="width: 100%" 
				stripe
				border
			>
				<el-table-column prop="creationTime" label="创建日期" width="180" align="center" />
				
				<el-table-column prop="fileName" label="文件名" min-width="200" show-overflow-tooltip>
					<template #default="scope">
						<span class="file-name">{{ scope.row.fileName }}</span>
					</template>
				</el-table-column>
				
				<el-table-column prop="latestStatus" label="状态" width="120" align="center">
					<template #default="scope">
						<el-tag :type="getStatusType(scope.row.latestStatus)" effect="light">
							{{ scope.row.latestStatus }}
						</el-tag>
					</template>
				</el-table-column>
				
				<el-table-column label="操作" width="120" align="center" fixed="right">
					<template #default="scope">
						<el-button 
							link 
							type="primary" 
							:disabled="!canDownload(scope.row.latestStatus)"
							@click="handleDownload(scope.row)"
						>
							<el-icon v-if="canDownload(scope.row.latestStatus)" class="btn-icon"><Download /></el-icon>
							{{ canDownload(scope.row.latestStatus) ? '下载' : '不可用' }}
						</el-button>
					</template>
				</el-table-column>
				
				<template #empty>
					<el-empty description="暂无下载记录" />
				</template>
			</el-table>

			<!-- 分页 -->
			<div class="pagination-container">
				<el-pagination
					v-model:current-page="currentPage"
					v-model:page-size="pageSize"
					:page-sizes="[10, 20, 50]"
					:total="total"
					layout="total, sizes, prev, pager, next"
					@size-change="handleSizeChange"
					@current-change="handleCurrentChange"
				/>
			</div>
		</el-card>
	</div>
</template>

<script>
	import { Search, Refresh, Download } from '@element-plus/icons-vue';

	export default {
		// 显式注册组件，以便在模板中使用 <Search /> 等标签
		components: {
			Search,
			Refresh,
			Download
		},
		data() {
			return {
				loading: false,
				downloadList: [],
				queryTime: [],
				currentPage: 1,
				pageSize: 10,
				total: 0,
				timer: null // 用于定时轮询
			};
		},
		created() {
			this.initDefaultTime();
			this.getDownloadList();
		},
		beforeUnmount() {
			// 组件销毁时清除定时器
			if (this.timer) clearInterval(this.timer);
		},
		methods: {
			initDefaultTime() {
				const end = new Date();
				const start = new Date();
				start.setDate(start.getDate() - 7);
				
				const formatDate = (date, isStart) => {
					const y = date.getFullYear();
					const m = String(date.getMonth() + 1).padStart(2, '0');
					const d = String(date.getDate()).padStart(2, '0');
					const time = isStart ? '00:00:00' : '23:59:59';
					return `${y}-${m}-${d} ${time}`;
				};
				
				this.queryTime = [formatDate(start, true), formatDate(end, false)];
			},

			getDownloadList() {
				this.loading = true;
				const [startTime, endTime] = this.queryTime || [];
				
				// 假设使用 apiService，如果原项目使用 axios，请替换为 this.axios.post(...)
				this.apiService.post({
					modules: "downloadList",
					content: {
						startTime,
						endTime,
						pageNum: this.currentPage,
						pageSize: this.pageSize
					}
				})
				.then((res) => {
					if (res.status) {
						this.downloadList = res.data.downloadList || [];
						this.total = res.data.total || 0;
						
						// 检查是否有正在处理中的任务，如果有则启动轮询
						this.checkAndStartPolling();
					}
				})
				.catch(err => {
					console.error(err);
					this.$message.error('获取列表失败');
				})
				.finally(() => {
					this.loading = false;
				});
			},

			handleSearch() {
				this.currentPage = 1;
				this.getDownloadList();
			},

			handleReset() {
				this.initDefaultTime();
				this.currentPage = 1;
				this.getDownloadList();
			},

			handleSizeChange(val) {
				this.pageSize = val;
				this.getDownloadList();
			},

			handleCurrentChange(val) {
				this.currentPage = val;
				this.getDownloadList();
			},

			// 判断状态类型以显示不同颜色的 Tag
			getStatusType(status) {
				if (!status) return 'info';
				if (status.includes('成功') || status.includes('完成')) return 'success';
				if (status.includes('失败') || status.includes('错误')) return 'danger';
				if (status.includes('处理') || status.includes('生成')) return 'warning';
				return 'info';
			},

			// 判断是否可以下载
			canDownload(status) {
				return status && (status.includes('成功') || status.includes('完成'));
			},

			handleDownload(row) {
				if (row.url) {
					window.location.href = row.url;
				} else {
					this.$message.warning('下载链接不存在');
				}
			},

			// 轮询逻辑：如果列表中有“处理中”的状态，每隔5秒刷新一次
			checkAndStartPolling() {
				const hasProcessing = this.downloadList.some(item => 
					item.latestStatus && (item.latestStatus.includes('处理') || item.latestStatus.includes('生成'))
				);

				if (hasProcessing) {
					if (!this.timer) {
						this.timer = setInterval(() => {
							// 静默刷新，不显示 loading
							this.loading = false; 
							const [startTime, endTime] = this.queryTime || [];
							this.apiService.post({
								modules: "downloadList",
								content: {
									startTime,
									endTime,
									pageNum: this.currentPage,
									pageSize: this.pageSize
								}
							}).then(res => {
								if (res.status) {
									this.downloadList = res.data.downloadList || [];
									// 如果所有任务都完成了，停止轮询
									const stillProcessing = this.downloadList.some(item => 
										item.latestStatus && (item.latestStatus.includes('处理') || item.latestStatus.includes('生成'))
									);
									if (!stillProcessing) {
										clearInterval(this.timer);
										this.timer = null;
									}
								}
							});
						}, 5000);
					}
				} else {
					if (this.timer) {
						clearInterval(this.timer);
						this.timer = null;
					}
				}
			}
		}
	};
</script>

<style scoped lang="scss">
	.download-center-page {
		padding: 20px;
		background-color: #f5f7fa;
		min-height: 100vh;
		display: flex;
		flex-direction: column;
		gap: 16px;
	}

	.search-card {
		.search-form {
			display: flex;
			align-items: center;
		}
	}

	.table-card {
		flex: 1;
		
		.file-name {
			font-weight: 500;
			color: #303133;
		}

		.btn-icon {
			margin-right: 4px;
			vertical-align: middle;
		}

		.pagination-container {
			margin-top: 20px;
			display: flex;
			justify-content: flex-end;
		}
	}
</style>